import * as React from "react";

import {
  DefaultOptions,
  QueryCache,
  QueryClient,
  QueryClientProvider as RQProvider,
} from "react-query";

/**
 * This is a clever little "hack" to make sure we make only one instance of
 * something for a given instance of the component. We store it in
 * lazily-initialized state and only return the state value and ignore the
 * state updater. This is the simplest way to do this.
 *
 * @param {Function} initializer A callback to initialize the value
 * @returns a constant value returned from the initializer
 */
function useConstant(initializer: () => any) {
  return React.useState(initializer)[0];
}

const defaultOptions: DefaultOptions = {
  queries: {
    refetchOnWindowFocus: false,
    retry(failureCount, error: any) {
      if (error?.status === 404) {
        // don't retry if response is 404
        return false;
      }
      if (failureCount < 2) {
        // only retry max 3 times on failures
        return true;
      }
      return false;
    },
  },
};

const queryCache = new QueryCache();
export const queryClient = new QueryClient({
  queryCache,
  defaultOptions,
});

export const QueryClientProvider: React.FC = ({ children }) => {
  const _queryClient = useConstant(() => {
    const client = queryClient
      ? queryClient
      : new QueryClient({
          queryCache,
          defaultOptions,
        });
    // let the devtools know about our new query client
    // if the devtools are installed:
    // @ts-ignore
    window?.__devtools?.setQueryClient?.(client);
    return client;
  });

  return <RQProvider client={_queryClient}>{children}</RQProvider>;
};
